<script lang="ts" setup>
import { TASK_STATE } from "@core/constants"
import { useTaskStore } from "@core/stores/task.store"
import { useI18n } from "vue-i18n"

const taskStore = useTaskStore()

/** État fictif pour les titres i18n : succès technique + flag métier FAILURE (sans « échoué »). */
const TASK_TITLE_STATE_INFORMATIONAL = "informational"

const { t, te } = useI18n({
    messages: {
        fr: {
            tasks: {
                task_export_buildings_sources: ({
                    named,
                }: {
                    named: (name: string) => string
                }) => {
                    const state = named("state")

                    if (state === TASK_TITLE_STATE_INFORMATIONAL) {
                        return "Export des données du parc"
                    }
                    if (state === TASK_STATE.SUCCESS) {
                        return "Export des données du parc terminé"
                    } else if (state === TASK_STATE.FAILURE) {
                        return "Export des données du parc échoué"
                    }

                    return "Export des données du parc..."
                },
                task_export_rt: ({
                    named,
                }: {
                    named: (name: string) => string
                }) => {
                    const state = named("state")

                    if (state === TASK_TITLE_STATE_INFORMATIONAL) {
                        return "Export des données du référentiel technique"
                    }
                    if (state === TASK_STATE.SUCCESS) {
                        return "Export des données terminé"
                    } else if (state === TASK_STATE.FAILURE) {
                        return "Export des données échoué"
                    }

                    return "Export des données du référentiel technique..."
                },
                workers2app_getlastdata_updateapp_request: ({
                    named,
                }: {
                    named: (name: string) => string
                }) => {
                    const state = named("state")

                    if (state === TASK_TITLE_STATE_INFORMATIONAL) {
                        return "Récupération des dernières données"
                    }
                    if (state === TASK_STATE.SUCCESS) {
                        return "Récupération des dernières données terminée"
                    } else if (state === TASK_STATE.FAILURE) {
                        return "Récupération des dernières données échouée"
                    }

                    return "Récupération des dernières données en cours..."
                },
            },
            task_default: "Export en cours...",
            download: "Télécharger le fichier",
            progress_steps:
                "Étape {current} sur {total} réalisée. Veuillez patienter...",
            success_url:
                "Le fichier est téléchargé automatiquement. Cliquer ici si ce n'est pas le cas.",
            success_task: "L'opération a été réalisée avec succès.",
            /** Échec pour les tâches d’export (`task_export_*`). */
            failure_task_export:
                "L'export n'a pas pu être réalisé. Veuillez réessayer plus tard si le problème persiste.",
            /** Échec lorsque la tâche n’a pas de `taskToastFailureMessage` et n’est pas un export. */
            failure_task_generic:
                "L'opération n'a pas pu être réalisée. Veuillez réessayer plus tard si le problème persiste.",
            failure_raison: "Raison de l'échec :",
            informational_detail_label: "Information :",
            failure_progress_bar: "Erreur",
            error: {
                "aucune-donnee": "Aucune donnée à exporter",
            },
        },
    },
})

/** Succès technique (`status` OK) + `flag` métier FAILURE : pas un échec d’exécution. */
function isInformationalOutcomeToast(taskId: string): boolean {
    const task = taskStore.getTaskById(taskId)
    if (!task || task.state !== TASK_STATE.SUCCESS) return false
    const ret = task.return
    return ret?.flag === "FAILURE" || ret?.result?.flag === "FAILURE"
}

/** Affichage du bloc « fin de tâche » type erreur (vrai échec ou information métier). */
function isFailureLikeToast(taskId: string): boolean {
    const task = taskStore.getTaskById(taskId)
    if (!task) return false
    if (task.state === TASK_STATE.FAILURE) return true
    return isInformationalOutcomeToast(taskId)
}

function isExportTaskName(taskname: string | undefined): boolean {
    if (!taskname) return false
    return taskname.startsWith("task_export")
}

/** Masque étapes / message de succès sous la barre (option tâche). */
function getTaskHideSubtitle(taskId: string): boolean {
    return Boolean(taskStore.getTaskById(taskId)?.taskToastHideSubtitle)
}

function getTaskName(taskId: string) {
    const task = taskStore.getTaskById(taskId)
    // 1. Valeur personnalisée depuis la tâche
    if (task?.taskToastTitle) {
        return task.taskToastTitle
    }
    const taskname = task?.taskname
    const state = isInformationalOutcomeToast(taskId)
        ? TASK_TITLE_STATE_INFORMATIONAL
        : task?.state
    // 2. Clé i18n enregistrée pour le taskname
    const i18nKey = taskname?.replace(/\./g, "_")
    if (i18nKey && te(`tasks.${i18nKey}`)) {
        return t(`tasks.${i18nKey}`, {
            state,
        })
    }
    // 3. Valeur par défaut
    return t("task_default")
}

function getTaskProgressByState(taskId: string) {
    const task = taskStore.getTaskById(taskId)
    const state = task?.state

    const GAP = 25

    switch (state) {
        case TASK_STATE.RECEIVED:
            return 10
        case TASK_STATE.STARTED:
            return GAP
        case TASK_STATE.PROGRESS: {
            const completed = (task?.progress_tasks ?? []).filter(
                (pt) => pt.state === TASK_STATE.SUCCESS
            ).length

            const total = task?.subtasks_total

            if (typeof total === "number" && total > 0) {
                return Math.min(89, Math.round(25 + (completed / total) * 65))
            }
            const progressTasks = task?.progress_tasks
            const minProgress = Math.round(
                GAP + (progressTasks?.length ?? 0) * GAP
            )

            let progress = minProgress

            const lastProgressTask = getLastProgressTask(taskId)

            if (lastProgressTask?.state === TASK_STATE.SUCCESS) {
                progress += GAP
            } else if (
                lastProgressTask &&
                lastProgressTask.nsteps != null &&
                lastProgressTask.nsteps > 0 &&
                lastProgressTask.step != null
            ) {
                const stepProgress =
                    (lastProgressTask.step / lastProgressTask.nsteps) * GAP
                progress += Math.round(stepProgress)
            }

            progress = progress < minProgress ? minProgress : progress

            return progress > 90 ? 90 : progress
        }
        case TASK_STATE.SUCCESS:
            return 100
        default:
            return 0
    }
}

/**
 * Détail sous-tâches (format Celery avec `subtasks_total`) ou message d’étape parent.
 */
function getSubtaskProgressLine(taskId: string): string {
    const task = taskStore.getTaskById(taskId)
    if (!task) return ""
    const completed = (task.progress_tasks ?? []).filter(
        (pt) => pt.state === TASK_STATE.SUCCESS
    ).length

    const total = task.subtasks_total

    if (typeof total === "number" && total > 0) {
        const inProgress = [...(task.progress_tasks ?? [])]
            .reverse()
            .find((pt) => pt.state === TASK_STATE.PROGRESS)
        const label =
            inProgress?.message ||
            inProgress?.label ||
            inProgress?.label ||
            task.current_step_message ||
            ""
        const base = `${completed} / ${total}`
        return label ? `${base} — ${label}` : ""
    }
    if ((task.progress_tasks?.length ?? 0) > 0) {
        const inProgress = [...(task.progress_tasks ?? [])]
            .reverse()
            .find((pt) => pt.state === TASK_STATE.PROGRESS)
        const label =
            inProgress?.message ||
            inProgress?.label ||
            inProgress?.label ||
            inProgress?.sub_taskname ||
            task.current_step_message ||
            ""
        return label ? `${completed} — ${label}` : ""
    }
    if (task.current_step_message && task.state === TASK_STATE.PROGRESS) {
        return task.current_step_message
    }
    return ""
}

function getLastProgressTask(taskId: string) {
    const task = taskStore.getTaskById(taskId)
    const progressTasks = task?.progress_tasks
    if (progressTasks && progressTasks.length > 0) {
        return progressTasks[progressTasks.length - 1]
    }
    return null
}

/**
 * Détail d’erreur affiché sous le message générique : message API / Pusher en priorité, puis code i18n.
 */
function getErrorReason(taskId: string) {
    const task = taskStore.getTaskById(taskId)
    const ret = task?.return
    const direct = ret?.message ?? ret?.data?.message
    if (typeof direct === "string" && direct.trim() !== "") {
        return direct.trim()
    }
    const code = ret?.error?.code ?? ret?.data?.detail?.code

    if (code && te(`error.${code}`)) {
        return t(`error.${code}`)
    }
    return ""
}

function isTaskError(taskId: string) {
    const task = taskStore.getTaskById(taskId)
    return task?.state === TASK_STATE.FAILURE
}

/**
 * Texte principal du bloc d’erreur (vrai FAILURE uniquement) : personnalisé, sinon export vs générique.
 */
function getFailurePrimaryLine(taskId: string): string {
    const task = taskStore.getTaskById(taskId)
    if (!task || task.state !== TASK_STATE.FAILURE) return ""
    if (task.taskToastFailureMessage) {
        return task.taskToastFailureMessage
    }
    if (isExportTaskName(task.taskname)) {
        return t("failure_task_export")
    }
    return t("failure_task_generic")
}

function getDetailCaption(taskId: string): string {
    return isInformationalOutcomeToast(taskId)
        ? t("informational_detail_label")
        : t("failure_raison")
}

/**
 * Message de succès affiché dans le toast : personnalisé (taskToastSuccessMessage) ou défaut i18n
 */
function getSuccessMessage(taskId: string) {
    const task = taskStore.getTaskById(taskId)
    if (task?.taskToastSuccessMessage) {
        return task.taskToastSuccessMessage
    }
    return t("success_task")
}

function onClose(taskId: string, callback: () => void) {
    callback()

    taskStore.removeTask(taskId)
}
</script>

<template>
    <BaseToast group="tasks" position="top-right" class="task-toast" closable>
        <template #container="{ message, closeCallback }">
            <div class="task-toast-content">
                <!-- Titre de la tâche et bouton fermer -->
                <div class="task-toast-content__header">
                    <h3 class="text-lg font-bold">
                        {{ getTaskName(message.detail) }}
                    </h3>
                    <BaseButton
                        icon="pi pi-times"
                        rounded
                        variant="text"
                        @click="onClose(message.detail, closeCallback)"
                    />
                </div>
                <!-- Barre de progression -->
                <BaseProgressBar
                    v-if="
                        !isFailureLikeToast(message.detail) &&
                        ![TASK_STATE.EMPTY, TASK_STATE.FAILURE].includes(
                            taskStore.getTaskById(message.detail)?.state ?? ''
                        )
                    "
                    :value="getTaskProgressByState(message.detail)"
                >
                    <span v-if="isTaskError(message.detail)">
                        {{ t("failure_progress_bar") }}
                    </span>
                </BaseProgressBar>
                <p
                    v-if="
                        !getTaskHideSubtitle(message.detail) &&
                        getSubtaskProgressLine(message.detail)
                    "
                    class="text-sm text-surface-600"
                >
                    {{ getSubtaskProgressLine(message.detail) }}
                </p>
                <div
                    v-if="
                        !getTaskHideSubtitle(message.detail) &&
                        getLastProgressTask(message.detail)?.nsteps
                    "
                >
                    <p class="text-sm">
                        {{
                            t("progress_steps", {
                                current:
                                    getLastProgressTask(message.detail)?.step ||
                                    0,
                                total:
                                    getLastProgressTask(message.detail)
                                        ?.nsteps || 0,
                            })
                        }}
                    </p>
                </div>
                <!-- URL de téléchargement -->
                <div
                    class="task-toast-content__url"
                    v-if="
                        taskStore.getTaskById(message.detail)?.return?.data
                            ?.file_url
                    "
                >
                    <p class="font-medium text-sm">{{ t("success_url") }}</p>
                    <BaseButton
                        as="a"
                        :label="t('download')"
                        target="_blank"
                        size="small"
                        :href="
                            taskStore.getTaskById(message.detail)?.return?.data
                                ?.file_url
                        "
                    />
                </div>
                <!-- Message de succès -->
                <div
                    v-if="
                        !getTaskHideSubtitle(message.detail) &&
                        taskStore.getTaskById(message.detail)?.state ===
                            TASK_STATE.SUCCESS &&
                        !isFailureLikeToast(message.detail)
                    "
                >
                    <p class="font-medium text-sm">
                        {{ getSuccessMessage(message.detail) }}
                    </p>
                </div>
                <!-- Vrai échec ou information métier (status OK + flag FAILURE) : pas de libellé « échoué » pour ce dernier cas -->
                <div
                    v-if="
                        taskStore.getTaskById(message.detail)?.state ===
                            TASK_STATE.FAILURE ||
                        isFailureLikeToast(message.detail)
                    "
                >
                    <p
                        v-if="getFailurePrimaryLine(message.detail)"
                        class="font-medium text-sm"
                    >
                        {{ getFailurePrimaryLine(message.detail) }}
                    </p>
                    <p
                        v-if="getErrorReason(message.detail)"
                        class="font-semibold"
                    >
                        {{ getDetailCaption(message.detail) }}<br />
                        <span class="font-medium text-sm">{{
                            getErrorReason(message.detail)
                        }}</span>
                    </p>
                </div>
            </div>
        </template>
    </BaseToast>
</template>

<style scoped>
.task-toast-content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
    .task-toast-content__url {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }
    .task-toast-content__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }
}
</style>
<style>
.task-toast-task-finished-failure {
    --p-toast-info-background: var(--p-toast-error-background) !important;
    --p-toast-info-border-color: var(--p-toast-error-border-color) !important;
    --p-toast-info-color: var(--p-toast-error-color) !important;
    --p-progressbar-value-background: var(
        --p-toast-error-border-color
    ) !important;
}
</style>
